import { Text } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import {
  CountryIsoCode,
  Engagement,
  EngagementRemoteWorkCostAllowancePolicyObjectType,
  Maybe,
  PaymentFrequencyEnum,
  ReimbursementPolicyEnum,
  VariableCompensationTypeEnum,
} from "@oysterhr/schema"
import { VariableCompensationSummary } from "~/shared/components/VariableCompensationSummary"
import { countryCodeToName, buildMoney } from "@oysterhr/common"
import { formatMoney } from "~/shared/components/VariableCompensationSummary/helpers"

type RemoteWorkCostAllowancesItemDisplayPartialProps = {
  allowanceRemoteWorkCost?: Maybe<EngagementRemoteWorkCostAllowancePolicyObjectType>
  salaryCurrencyCode: Engagement["payrollCurrencyCode"]
  countryCode?: Maybe<CountryIsoCode>
}

export const RemoteWorkCostAllowancesItemDisplayPartial = ({
  allowanceRemoteWorkCost,
  salaryCurrencyCode,
  countryCode,
}: RemoteWorkCostAllowancesItemDisplayPartialProps) => {
  const [t] = useTranslation("company_interface/CompanyNewHireFTEPage")
  const allowanceRequiresPayment =
    allowanceRemoteWorkCost?.allowancePolicy === ReimbursementPolicyEnum.MonthlyAllowance ||
    allowanceRemoteWorkCost?.allowancePolicy === ReimbursementPolicyEnum.OneTimeStipend

  const currencyCode = salaryCurrencyCode || "USD"
  const countryName = countryCodeToName(countryCode || null)

  return (
    <>
      {allowanceRequiresPayment && (
        <div className="mt-2">
          <hr />
          <div className="pt-4">
            <VariableCompensationSummary
              compensationType={VariableCompensationTypeEnum.Allowance}
              type="amount"
              amount={{
                subunit: allowanceRemoteWorkCost?.allowancePolicySubunit || 0,
                currencyCode,
              }}
              frequency={
                allowanceRemoteWorkCost?.allowancePolicy ===
                ReimbursementPolicyEnum.MonthlyAllowance
                  ? PaymentFrequencyEnum.Monthly
                  : PaymentFrequencyEnum.OneTime
              }
              displayProratedSummary={false}
              prorated={false}
            />
            {allowanceRemoteWorkCost.isStatutory && (
              <div className="-mt-2 text-gray-400">
                <div className="flex w-full justify-between">
                  <Text size="small" as="span">
                    {t("remoteWorkCosts.allowancePolicy.labels.amountStatutoryInCountry", {
                      countryName,
                    })}
                  </Text>
                  <Text size="small" as="span" className="whitespace-nowrap">
                    {formatMoney(
                      buildMoney({
                        subunit: allowanceRemoteWorkCost.statutoryPolicySubunit,
                        currencyCode,
                      }),
                    )}
                  </Text>
                </div>
                <div className="flex w-full justify-between">
                  <Text size="small" as="span">
                    {t("remoteWorkCosts.allowancePolicy.labels.amountOfferedAboveStatutory")}
                  </Text>
                  <Text size="small" as="span" className="whitespace-nowrap">
                    {formatMoney(
                      buildMoney({
                        subunit: allowanceRemoteWorkCost.topUpPolicySubunit,
                        currencyCode,
                      }),
                    )}
                  </Text>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {allowanceRemoteWorkCost?.allowancePolicy === ReimbursementPolicyEnum.BasicReimbursement && (
        <p className="pt-4 text-sm text-gray-400">
          {t("inlineAlerts.reimbursementPolicies.BASIC_REIMBURSEMENT")}
        </p>
      )}
    </>
  )
}
